import { buildPath } from '../../services/navigateService.js';
import { updateUserPageColumns } from '../../services/userService.js';
import { SHOW_COLUMNS_DEFAULT_VALUES, TABLE_LOCATIONS, USER_ROLE } from '../../static/constants.js';
import { NAVIGATION_PATHS } from '../../static/navigationPaths.js';

export const getPageColumnsForUpdate = (newUserColumnsToShow) => {
  const newUserColumnsToShowKeys = Object.keys(newUserColumnsToShow);
  let columns = [];
  for (const columnLocationKey of newUserColumnsToShowKeys) {
    const location = Object.keys(TABLE_LOCATIONS).find(
      (key) => TABLE_LOCATIONS[key] === columnLocationKey
    );
    const userColumns = newUserColumnsToShow[columnLocationKey];
    userColumns.forEach((userColumn) => {
      if (userColumn?.show) {
        columns.push({
          columnName: userColumn.name,
          location: location,
        });
      }
    });
  }
  return columns;
};

export const totalCostFormat = (number) => {
  const value = Number(number);

  if (isNaN(value)) {
    return number;
  }

  const isNegative = value < 0;
  const prefix = isNegative ? '-$' : '$';

  return (
    prefix +
    new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(Math.abs(value))
  );
};

export const numberFormat = (number) => {
  const value = Number(number);

  if (isNaN(value)) {
    return number;
  }

  return (
    new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(value)
  );
};

export const percentageFormat = (value) => {
  return `${numberFormat(value)}%`;
};

export const isNotNullOrUndefined = (item) => {
  return item != null && item != 'undefined';
};

export const roundToTwoDecimals = (number) => {
  return Number.parseFloat(number).toFixed(2);
};

export const getAccessCondition = (
  iconName,
  organizationAccessIcons,
  user
) => {
  let access = false;
  if (iconName.toLowerCase() === 'home' || user?.role === 'admin user') {
    access = true;
  } else if (user?.role === 'super user') {
    const org = organizationAccessIcons.find(
      (appIcon) => appIcon.name === iconName
    );
    access = org?.organization && org?.superUser;
  } else if (user?.role === 'department user') {
    const org = organizationAccessIcons.find(
      (appIcon) => appIcon.name === iconName
    );
    access = org?.organization && org?.departmentUser;
  }
  return access;
};

export const checkIfNotEmpty = (arr, organizationAccessIcons, user) => {
  if (arr && arr.length > 0) {
    return arr.some((item) => getAccessCondition(item.name, organizationAccessIcons, user));
  }
  return false;
};

export const getValueByPath = (obj, path) => {
  return path
    .replace(/\[(\w+)\]/g, '.$1') // convert [key] to .key
    .replace(/^\./, '')           // remove leading dot if any
    .split('.')
    .reduce((acc, key) => acc?.[key], obj);
};

export const setValueByPath = (obj, path, value) => {
  if (!obj || typeof path !== 'string') return;

  path = path.replace(/\[(\w+)\]/g, '.$1').replace(/^\./, '');
  const keys = path.split('.');

  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    const nextKey = keys[i + 1];

    if (!(key in current) || typeof current[key] !== 'object' || current[key] === null) {
      current[key] = /^\d+$/.test(nextKey) ? [] : {};
    }

    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
};

export const fieldSpaceValidator = (field) => {
  const pattern = /^(?! )(?!.* {2})[A-Za-z0-9:,_ -]{3,50}(?<! )$/;
  return pattern.test(field);
};

//option builder for menu or select option list with icons - BACKUP - not currently used
export const buildMenuOption = (option) => {
  const icon = document.createElement('forge-icon');
  icon.setAttribute('slot', 'start');
  icon.setAttribute('external', '');
  icon.setAttribute('external-type', 'all');
  icon.setAttribute('name', option.iconName);

  const div = document.createElement('div');
  div.style.display = 'flex';
  div.style.alignItems = 'center';
  div.style.gap = '16px';
  div.appendChild(icon);
  div.appendChild(document.createTextNode(option.label));

  return div;
};

export const constructMissingColumnsMessage = (errorCols) => {
  return 'The following columns are missing from the file:\n' +
    errorCols.map(col => `• ${col}`).join('\n');
};

export const normalizeColumnName = (column) =>
  column === null || column === undefined ? '' : String(column).trim().toLowerCase();

// matches file headers to the expected names ignoring case/spacing, unknown ones keep their own name
export const normalizeColumnsToExpected = (columns, expectedColumns) => {
  const canonicalNames = new Map();
  (expectedColumns || []).forEach((expectedColumn) => {
    const normalized = normalizeColumnName(expectedColumn);
    if (normalized && !canonicalNames.has(normalized)) {
      canonicalNames.set(normalized, expectedColumn);
    }
  });

  const takenKeys = new Set();
  const keyMap = new Map();
  (columns || []).forEach((fileColumn) => {
    const canonical = canonicalNames.get(normalizeColumnName(fileColumn));
    let key = canonical && !takenKeys.has(canonical) ? canonical : fileColumn;
    let suffix = 2;
    while (takenKeys.has(key)) {
      key = `${canonical || fileColumn} (${suffix})`;
      suffix++;
    }
    takenKeys.add(key);
    keyMap.set(fileColumn, key);
  });

  return { columns: [...takenKeys], keyMap };
};

export const normalizeRowsToExpectedColumns = (rows, expectedColumns) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { rows: Array.isArray(rows) ? rows : [], columns: [] };
  }

  const { columns, keyMap } = normalizeColumnsToExpected(
    Object.keys(rows[0]),
    expectedColumns
  );

  const normalizedRows = rows.map((row) => {
    const normalizedRow = {};
    Object.keys(row).forEach((fileColumn) => {
      normalizedRow[keyMap.get(fileColumn) ?? fileColumn] = row[fileColumn];
    });
    return normalizedRow;
  });

  return { rows: normalizedRows, columns };
};

export const arraysAreEqual = (a, b) => {
  const [setA, setB] = [new Set(a), new Set(b)];
  if (setA.size !== setB.size) return false;
  return [...setA].every(v => setB.has(v));
};

export const updateUserColumns = (e, _columnKey, user, setUser, setShowColumns, showColumns) => {
  let storedColumns = showColumns;
  const defaultColumns = SHOW_COLUMNS_DEFAULT_VALUES[_columnKey];
  const storedColumnList = storedColumns[_columnKey] || [];
  let columnsEntity = defaultColumns.map((defaultCol) => {
    const storedCol = storedColumnList.find((col) => col.name === defaultCol.name);

    return storedCol
      ? { ...defaultCol, ...storedCol }
      : defaultCol;
  });

  storedColumns[_columnKey] = columnsEntity;

  if (e.column) {
    const column = e.column.colDef.context;
    const visible = e.visible;
    let changedColumn = columnsEntity.find(
      (col) => col.name === column
    );
    if (changedColumn) {
      changedColumn.show = visible;
    } else {
      columnsEntity.push({
        name: column,
        show: visible
      });
    }
  } else {
    let changedColumns = e.columns.map(c => {
      return { name: c.colDef.context, show: c.visible };
    });

    changedColumns.forEach(changedCol => {
      let col = columnsEntity.find(c => c.name === changedCol.name);
      if (col) {
        col.show = changedCol?.show;
      }
    });
    storedColumns[_columnKey] = columnsEntity;
  }
  //setShowColumns(storedColumns);
  updateUserPageColumns(getPageColumnsForUpdate(storedColumns)).then(() => {
    // const _user = structuredClone(user);
    // _user.showColumns = storedColumns;
    // setUser(_user);
    // if (forceUpdateColDef) {
    //   forceUpdateColDef();
    // }
  });
};

export const checkErrorState = (gridApi) => {
  const errorRows = [];
  gridApi.forEachNode((node) => {
    if (node.data.Error) {
      errorRows.push(node.data);
    }
  });
  return errorRows;
};

export const checkErrorStateUpload = (data) => {
  const errorRows = [];
  data.forEach((row) => {
    if (row.Error) {
      errorRows.push(row);
    }
  });
  return errorRows;
};

export const getBadgeColor = (type, val) => {
  if (type === 'role') {
    switch (val.toLowerCase()) {
      case USER_ROLE.SUPER_USER:
        return 'info';
      case USER_ROLE.DEPARTMENT_USER:
        return 'success';
      default:
        return '';
    }
  } else if (type === 'access') {
    if (val) {
      return 'info-secondary';
    } else {
      return 'info';
    }
  }
};

export const postSortRows = (params, prop) => {
  const rowNodes = params?.nodes;
  if (!rowNodes || !rowNodes.length) return;

  rowNodes.sort((a, b) => {
    const aData = a?.data || {};
    const bData = b?.data || {};
    const aError = Boolean(aData.Error);
    const bError = Boolean(bData.Error);

    if (aError !== bError) {
      return aError ? -1 : 1;
    }

    if (prop && !aError && !bError) {
      const aProp = Boolean(aData[prop]);
      const bProp = Boolean(bData[prop]);

      if (aProp !== bProp) {
        return aProp ? -1 : 1;
      }
    }
    return 0;
  });
};

export const textCase = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const scrollToTop = (className) => {
  const element = document.getElementsByClassName(className)[0];
  if (element) {
    element.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

export const handleKeyDown = (e, callback) => {
  if (e.key === 'Enter') {
    callback();
  } else {
    return;
  }
};

export const budgetListSort = (budgetsListToSort) => {
  const budgetListValues = Object.values(budgetsListToSort);
  return budgetListValues.sort((a, b) => {
    if (a.order && b.order) {
      return a.order - b.order;
    }
    if (a.order) {
      return -1;
    }
    if (b.order) {
      return 1;
    }
    if (a.id && b.id) {
      return b.id - a.id;
    }
    return 0;
  });
};

export const handleUnauth = (error, navigate, user) => {

  if (error.response.status === 401) {
    window.localStorage.clear();
    window.sessionStorage.clear();
    const path = buildPath(user, NAVIGATION_PATHS.SESSION_TIMEOUT);

    navigate(path);
  }
};

export const createRandomID = () => crypto.randomUUID();

export const getIconByName = (organizationAccess, iconName) => {
  return organizationAccess?.find(icon => icon.name === iconName);
};

export function splitByDelimiter(value, delimiter = '--', part = 0) {
  if (!value) return '';
  const parts = value.split(delimiter);
  return parts[part] ?? '';
}

export const getOrganizationNameFromLocationPath = (locationPath) => {
  return locationPath.split('/')[1];
};

export const trimValue = (value) => {
  if (typeof value === 'string') {
    return value.trim();
  }
  return value;
};
